// src/api/bookings.js
import axios from "axios";
import { API_BASE as SBIN_API_BASE } from "./base";

const API = SBIN_API_BASE;

const authHeaders = () => ({
  Authorization: `Bearer ${null}`,
  "Content-Type": "application/json",
});

/**
 * Create a booking.
 *
 * The `data` object MUST include:
 *   {
 *     booth_id: number,
 *     start_time: ISO timestamp string,
 *     end_time: ISO timestamp string,
 *   }
 *
 * It MAY ALSO include dynamic add-on selections, for example:
 *   {
 *     selected_addons: [
 *       {
 *         key: "wax_room",
 *         label: "Waxing room access",
 *         quantity: 1,
 *         unit_price: 15.0,
 *         line_total: 15.0
 *       },
 *       ...
 *     ]
 *   }
 *
 * Any extra fields in `data` are passed straight through to the backend.
 */
export async function createBooking(data) {
  try {
    const res = await axios.post(`${API}/bookings/`, data, {
      headers: authHeaders(),
    });
    // We return the decoded JSON body (booking object)
    return res.data;
  } catch (err) {
    console.error("Booking API error:", err);

    // Bubble backend error message if available
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }

    throw new Error("Failed to create booking.");
  }
}

/**
 * Get booking details by ID.
 *
 * Returns a booking object that may include:
 *  - total_amount
 *  - addons_total_amount
 *  - selected_addons: [{ key, label, quantity, unit_price, line_total }, ...]
 */
export async function getBookingById(id) {
  try {
    const res = await axios.get(`${API}/bookings/${id}`, {
      headers: authHeaders(),
    });
    return res.data;
  } catch (err) {
    console.error("Get booking error:", err);
    throw new Error("Unable to load booking details.");
  }
}

/**
 * Get upcoming bookings for a specific booth (for mini calendar, availability, etc.).
 */
export async function getUpcomingBoothBookings(boothId) {
  try {
    const res = await axios.get(
      `${API}/bookings/booth/${boothId}/upcoming`,
      {
        headers: authHeaders(),
      }
    );
    return res.data;
  } catch (err) {
    console.error("Get upcoming booth bookings error:", err);

    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }

    throw new Error("Unable to load upcoming bookings.");
  }
}

export async function getMyBookings(token) {
  try {
    const res = await axios.get(`${API}/bookings/my`, {
      headers: {
        Authorization: `Bearer ${token || null}`,
      },
    });
    return res.data;
  } catch (err) {
    console.error("Get my bookings error:", err);
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Unable to load your bookings.");
  }
}

export async function getMyAppointments(token, params = {}) {
  try {
    const res = await axios.get(`${API}/appointments/my`, {
      headers: {
        Authorization: `Bearer ${token || null}`,
      },
      params,
    });
    return res.data;
  } catch (err) {
    console.error("Get my appointments error:", err);
    if (err.response?.data?.detail) {
      throw new Error(err.response.data.detail);
    }
    throw new Error("Unable to load your appointments.");
  }
}
